// The hook (openingen.md: opening 3 "Het signaal" + opening 2 "De gevonden telefoon").
// The player tunes into a weak signal before reading anything: they *do* something first.

import { sfx, vibrate } from '../audio.js';
import { backstage } from '../backstage.js';
import { $, el, sleep } from '../util.js';

const LINES = ['…is er iemand…', '…dit kanaal is van Ellen…', '…als je dit leest…'];
const NOISE = '▒░▓·:#%/\\';

function garble(text: string, clarity: number): string {
  return [...text].map((ch) => (ch === ' ' || Math.random() < clarity ** 1.6 ? ch : NOISE[Math.floor(Math.random() * NOISE.length)])).join('');
}

export function runSignal(): Promise<void> {
  return new Promise((resolve) => {
    const view = $('#view-signal');
    const target = 0.18 + Math.random() * 0.64;
    let value = target > 0.5 ? 0.08 : 0.92;
    let clarity = 0;
    let lockedSince = 0;
    let done = false;
    let lastStep = -1;

    const input = el('input', { type: 'range', min: 0, max: 1000, value: Math.round(value * 1000), 'aria-label': 'Stem af op het signaal' });
    const needle = el('div.tuner__needle');
    const freq = el('strong', {}, '');
    const strength = el('strong', {}, '');
    const lines = LINES.map(() => el('p.dim', {}, ''));
    const hint = el('p.signal__hint', {}, 'Sleep om af te stemmen');
    const canvas = el('canvas.signal__wave', { width: 700, height: 180 });
    view.replaceChildren(
      el('div.signal__brand', {}, el('span', {}, 'KANAAL'), el('span', {}, 'geen verbinding')),
      el('div.signal__text', {}, ...lines),
      canvas,
      el('div.signal__readout', {}, el('span', {}, 'frequentie ', freq), el('span', {}, 'signaal ', strength)),
      el('div.tuner', {}, el('div.tuner__scale'), needle, input),
      hint,
    );

    const ctx = canvas.getContext('2d')!;
    let phase = 0;
    const draw = () => {
      if (done && clarity >= 1) return;
      const { width: w, height: h } = canvas;
      ctx.clearRect(0, 0, w, h);
      ctx.lineWidth = 3;
      ctx.strokeStyle = clarity > 0.97 ? '#8fd694' : `rgba(255, ${106 + clarity * 80}, 26, ${0.5 + clarity * 0.5})`;
      ctx.beginPath();
      phase += 0.18;
      for (let x = 0; x <= w; x += 4) {
        const sine = Math.sin(x / 26 + phase) * Math.sin(x / 140 + phase * 0.3) * h * 0.32 * clarity;
        const noise = (Math.random() - 0.5) * h * 0.8 * (1 - clarity) ** 1.3;
        const y = h / 2 + sine + noise;
        x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }
      ctx.stroke();
      requestAnimationFrame(draw);
    };
    requestAnimationFrame(draw);

    const update = () => {
      value = +input.value / 1000;
      needle.style.left = `${value * 100}%`;
      freq.textContent = `${(87.5 + value * 20.5).toFixed(1)} MHz`;
      clarity = Math.max(0, 1 - Math.abs(value - target) / 0.3);
      strength.textContent = `${Math.round(clarity * 100)}%`;
      sfx.setStatic(clarity);
      // Small "detent" clicks while tuning
      const step = Math.round(value * 60);
      if (step !== lastStep) { lastStep = step; if (clarity > 0.85) sfx.tick(); }
      hint.textContent = clarity > 0.92 ? 'Hou vast…' : clarity > 0.6 ? 'Bijna. Langzaam.' : clarity > 0.2 ? 'Er is iets…' : 'Sleep om af te stemmen';
    };

    const flicker = window.setInterval(() => {
      lines.forEach((p, i) => {
        const c = Math.max(0, clarity - i * 0.08);
        p.textContent = garble(LINES[i], c);
        p.className = c > 0.75 ? '' : 'dim';
      });
      if (done) return;
      if (Math.abs(value - target) < 0.03) {
        lockedSince ||= performance.now();
        if (performance.now() - lockedSince > 900) void finish();
      } else lockedSince = 0;
    }, 110);

    async function finish() {
      done = true;
      clearInterval(flicker);
      input.disabled = true;
      clarity = 1;
      view.classList.add('is-locked');
      lines.forEach((p, i) => { p.textContent = LINES[i]; p.className = ''; });
      hint.textContent = 'Verbinding gevonden';
      ($('.signal__brand span:last-child', view)).textContent = 'verbonden';
      sfx.lock(); vibrate([20, 40, 20]);
      sfx.stopStatic();
      backstage.log('sensor', 'Opening: afstemmen', 'De speler doet iets nog vóór hij iets leest (opening 3, <em>het signaal</em>). Ruis en golf worden live gemaakt met Web Audio en canvas: geen beeld- of geluidsbestanden.');
      await sleep(1400);
      resolve();
    }

    input.addEventListener('input', update);
    sfx.startStatic();
    update();
  });
}
